import React, { useState, useEffect } from 'react';
import { getEmbedding } from '../services/geminiService';
import { validateToken } from '../services/githubService';

const SearchForm = ({ onSearch, onFilter }) => {
  const [username, setUsername] = useState(() => {
    // 从 localStorage 获取缓存的用户名
    return localStorage.getItem('github_username') || '';
  });
  const [keyword, setKeyword] = useState(() => {
    // 从 localStorage 获取缓存的搜索关键字
    return localStorage.getItem('github_search_keyword') || '';
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hasRepos, setHasRepos] = useState(false);
  // 不从 localStorage 获取令牌，确保每次页面加载时令牌都是空的
  const [token, setToken] = useState('');
  const [showTokenInput, setShowTokenInput] = useState(false);
  const [tokenStatus, setTokenStatus] = useState({ isValid: false, message: '' });
  const [useSemanticSearch, setUseSemanticSearch] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [apiKeyStatus, setApiKeyStatus] = useState({ isValid: false, message: '' });
  // 添加相似度阈值状态
  const [threshold, setThreshold] = useState(0.7);
  // 添加缓存的相似度结果状态
  const [cachedResults, setCachedResults] = useState([]);
  // 添加是否包含README的状态
  const [includeReadme, setIncludeReadme] = useState(false);
  const [searchProgress, setSearchProgress] = useState({
    phase: null,
    total: 0,
    current: 0,
    currentRepo: '',
    error: null
  });
  // 添加README加载进度状态
  const [readmeProgress, setReadmeProgress] = useState({
    phase: null,
    total: 0,
    current: 0,
    currentRepo: '',
    error: null
  });

  // 当用户名变化时，更新 localStorage
  useEffect(() => {
    localStorage.setItem('github_username', username);
  }, [username]);

  // 当搜索关键字变化时，更新 localStorage
  useEffect(() => {
    localStorage.setItem('github_search_keyword', keyword);
  }, [keyword]);

  // 当token变化时，更新 localStorage
  useEffect(() => {
    if (token) {
      localStorage.setItem('github_token', token);
      // 添加输入防抖，避免频繁验证
      clearTimeout(window.tokenValidationTimeout);
      window.tokenValidationTimeout = setTimeout(() => {
        validateGithubToken(token);
      }, 500);
    } else {
      setTokenStatus({ isValid: false, message: '' });
    }
  }, [token]);

  // 添加进度监听器
  useEffect(() => {
    const handleProgress = (event) => {
      setSearchProgress(event.detail);
    };

    const handleReadmeProgress = (event) => {
      setReadmeProgress(event.detail);
    };

    window.addEventListener('semanticSearchProgress', handleProgress);
    window.addEventListener('readmeProgress', handleReadmeProgress);
    
    return () => {
      window.removeEventListener('semanticSearchProgress', handleProgress);
      window.removeEventListener('readmeProgress', handleReadmeProgress);
    };
  }, []);

  // 验证Gemini API密钥
  const validateApiKey = async (key) => {
    if (!key) {
      setApiKeyStatus({ isValid: false, message: '请输入API密钥' });
      return;
    }

    try {
      // 尝试使用API密钥进行一次简单的嵌入请求
      await getEmbedding('test', key);
      setApiKeyStatus({ isValid: true, message: '✅ API密钥有效' });
      return true;
    } catch (error) {
      setApiKeyStatus({ isValid: false, message: '❌ API密钥无效：' + error.message });
      return false;
    }
  };

  // 处理API密钥变化
  const handleApiKeyChange = async (e) => {
    const newApiKey = e.target.value;
    setApiKey(newApiKey);
    if (newApiKey) {
      // 添加输入防抖，避免频繁验证
      clearTimeout(window.apiKeyValidationTimeout);
      window.apiKeyValidationTimeout = setTimeout(() => {
        validateApiKey(newApiKey);
      }, 500);
    } else {
      setApiKeyStatus({ isValid: false, message: '' });
    }
  };

  // 验证GitHub令牌
  const validateGithubToken = async (token) => {
    if (!token) {
      setTokenStatus({ isValid: false, message: '' });
      return;
    }

    try {
      // 使用githubService中的validateToken函数
      const isValid = await validateToken(token);
      if (isValid) {
        setTokenStatus({ isValid: true, message: '✅ GitHub令牌有效，API请求不受限制' });
      } else {
        setTokenStatus({ isValid: false, message: '❌ GitHub令牌无效，将使用未认证的API请求（受限制）' });
      }
    } catch (error) {
      setTokenStatus({ isValid: false, message: '❌ 验证GitHub令牌时出错：' + error.message });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) return;
    
    setIsLoading(true);
    try {
      await onSearch(username, token, tokenStatus.isValid);
      setHasRepos(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterSubmit = async (e) => {
    e.preventDefault();
    if (useSemanticSearch && !apiKeyStatus.isValid) {
      // 如果使用语义搜索但API密钥无效，先验证API密钥
      const isValid = await validateApiKey(apiKey);
      if (!isValid) {
        return; // 如果API密钥无效，不执行搜索
      }
    }
    
    setIsLoading(true);
    try {
      // 执行搜索并缓存结果
      const results = await onFilter(keyword, useSemanticSearch, apiKey, threshold, null, includeReadme);
      if (results && Array.isArray(results)) {
        setCachedResults(results);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 清除缓存的函数
  const clearCache = () => {
    localStorage.removeItem('github_username');
    localStorage.removeItem('github_search_keyword');
    localStorage.removeItem('github_token');
    setUsername('');
    setKeyword('');
    setToken('');
    setUseSemanticSearch(false);
    setApiKey('');
    setApiKeyStatus({ isValid: false, message: '' });
    setThreshold(0.7);
    setCachedResults([]);
    setIncludeReadme(true);
    setHasRepos(false);
  };

  // 处理语义搜索开关变化
  const handleSemanticSearchChange = (e) => {
    setUseSemanticSearch(e.target.checked);
    if (!e.target.checked) {
      setApiKey('');
      setApiKeyStatus({ isValid: false, message: '' });
    }
  };

  // 处理包含README选项变化
  const handleIncludeReadmeChange = (e) => {
    setIncludeReadme(e.target.checked);
  };

  // 处理相似度阈值变化
  const handleThresholdChange = (e) => {
    const newThreshold = parseFloat(e.target.value);
    setThreshold(newThreshold);
    
    // 如果有缓存的结果，实时更新显示
    if (cachedResults.length > 0) {
      // 根据新阈值过滤并更新显示
      onFilter(keyword, useSemanticSearch, apiKey, newThreshold, cachedResults, includeReadme);
    }
  };

  // 计算匹配的仓库数量
  const getMatchingReposCount = () => {
    if (!cachedResults || cachedResults.length === 0) return 0;
    
    if (useSemanticSearch) {
      // 语义搜索模式：根据阈值计算
      return cachedResults.filter(item => item.similarity >= threshold).length;
    } else {
      // 关键字搜索模式：计算相似度大于0的仓库
      return cachedResults.filter(item => item.similarity > 0).length;
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
      <form onSubmit={handleSubmit} className="mb-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-grow">
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
              GitHub 用户名
            </label>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-grow">
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="输入GitHub用户名"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  支持分页加载，将递归获取用户所有的 Star 仓库
                </p>
              </div>
              <div className="self-start">
                <button
                  type="submit"
                  className={`w-full md:w-auto px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isLoading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                  disabled={isLoading}
                >
                  {isLoading ? '加载中...' : '查看Star仓库'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <button 
            type="button" 
            onClick={() => setShowTokenInput(!showTokenInput)}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
          >
            {showTokenInput ? '隐藏令牌输入' : '使用GitHub个人访问令牌 (可选)'}
          </button>
          
          {showTokenInput && (
            <div className="mt-2">
              <label htmlFor="token" className="block text-sm font-medium text-gray-700 mb-1">
                GitHub 个人访问令牌
              </label>
              <input
                type="password"
                id="token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="输入GitHub个人访问令牌（可选）"
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  tokenStatus.isValid 
                    ? 'border-green-300 focus:ring-green-500' 
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
              />
              {tokenStatus.message && (
                <p className={`mt-2 p-2 rounded-md ${
                  tokenStatus.isValid 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {tokenStatus.message}
                </p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                使用令牌可以提高API请求限制，避免被限流。出于安全考虑，您的令牌不会被存储在本地或服务器上。
              </p>
            </div>
          )}
        </div>
        
        {/* 添加清除缓存按钮 */}
        <div className="mt-4">
          <button 
            type="button" 
            onClick={clearCache}
            className="text-red-600 hover:text-red-800 text-sm font-medium"
          >
            清除缓存的用户名和搜索记录
          </button>
        </div>
      </form>

      {/* 添加进度显示 */}
      {searchProgress.phase && (
        <div className="mt-4 bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-medium text-blue-800">
              {searchProgress.phase === 'preparing' && '准备处理...'}
              {searchProgress.phase === 'processing' && '正在进行语义搜索'}
              {searchProgress.phase === 'complete' && '搜索完成'}
              {searchProgress.phase === 'error' && '搜索出错'}
            </h3>
            {searchProgress.phase === 'processing' && (
              <span className="text-sm text-blue-600">
                {Math.round((searchProgress.current / searchProgress.total) * 100)}%
              </span>
            )}
          </div>
          
          {searchProgress.phase === 'processing' && (
            <>
              <div className="w-full bg-blue-200 rounded-full h-2.5 mb-2">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${(searchProgress.current / searchProgress.total) * 100}%` }}
                ></div>
              </div>
              <div className="text-sm text-blue-600">
                正在处理: {searchProgress.currentRepo}
                <br />
                进度: {searchProgress.current} / {searchProgress.total}
              </div>
            </>
          )}
          
          {searchProgress.error && (
            <div className="mt-2 text-sm text-red-600">
              错误: {searchProgress.error}
            </div>
          )}
        </div>
      )}

      {/* 添加README加载进度显示 */}
      {readmeProgress.phase && (
        <div className="mt-4 bg-green-50 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-medium text-green-800">
              {readmeProgress.phase === 'preparing' && '准备获取README...'}
              {readmeProgress.phase === 'processing' && '正在获取仓库README'}
              {readmeProgress.phase === 'complete' && 'README获取完成'}
              {readmeProgress.phase === 'error' && 'README获取出错'}
            </h3>
            {readmeProgress.phase === 'processing' && (
              <span className="text-sm text-green-600">
                {Math.round((readmeProgress.current / readmeProgress.total) * 100)}%
              </span>
            )}
          </div>
          
          {readmeProgress.phase === 'processing' && (
            <>
              <div className="w-full bg-green-200 rounded-full h-2.5 mb-2">
                <div 
                  className="bg-green-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${(readmeProgress.current / readmeProgress.total) * 100}%` }}
                ></div>
              </div>
              <div className="text-sm text-green-600">
                正在获取: {readmeProgress.currentRepo}
                <br />
                进度: {readmeProgress.current} / {readmeProgress.total}
                <br />
                <span className="text-xs text-gray-500">（获取大量README可能需要较长时间，请耐心等待）</span>
              </div>
            </>
          )}
          
          {readmeProgress.error && (
            <div className="mt-2 text-sm text-red-600">
              错误: {readmeProgress.error}
            </div>
          )}
        </div>
      )}

      {hasRepos && (
        <form onSubmit={handleFilterSubmit}>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-grow">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                搜索仓库
              </label>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-grow">
                  <input
                    type="text"
                    id="search"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="输入关键字搜索仓库"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  
                  {/* 添加搜索选项 */}
                  <div className="mt-2 flex flex-wrap items-center gap-4">
                    {/* 语义搜索选项 */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="semanticSearch"
                        checked={useSemanticSearch}
                        onChange={handleSemanticSearchChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="semanticSearch" className="ml-2 block text-sm text-gray-700">
                        使用Gemini语义搜索
                      </label>
                    </div>
                    
                    {/* 包含README选项 */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="includeReadme"
                        checked={includeReadme}
                        onChange={handleIncludeReadmeChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="includeReadme" className="ml-2 block text-sm text-gray-700">
                        包含README内容
                      </label>
                    </div>
                  </div>
                  
                  {/* 当勾选语义搜索时显示API密钥输入框和相似度阈值滑块 */}
                  {useSemanticSearch && (
                    <>
                      <div className="mt-2">
                        <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700 mb-1">
                          Gemini API密钥
                        </label>
                        <input
                          type="password"
                          id="apiKey"
                          value={apiKey}
                          onChange={handleApiKeyChange}
                          placeholder="输入您的Gemini API密钥"
                          className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                            apiKeyStatus.isValid 
                              ? 'border-green-300 focus:ring-green-500' 
                              : 'border-gray-300 focus:ring-blue-500'
                          }`}
                          required={useSemanticSearch}
                        />
                        {apiKeyStatus.message && (
                          <p className={`mt-2 p-2 rounded-md ${
                            apiKeyStatus.isValid 
                              ? 'bg-green-50 text-green-700 border border-green-200' 
                              : 'bg-red-50 text-red-700 border border-red-200'
                          }`}>
                            {apiKeyStatus.message}
                          </p>
                        )}
                        <p className="mt-1 text-sm text-gray-500">
                          您可以从 <a href="https://makersuite.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google AI Studio</a> 获取API密钥。
                          出于安全考虑，API密钥不会被存储，页面刷新后需要重新输入。
                        </p>
                      </div>
                      
                      {/* 添加相似度阈值滑块 */}
                      <div className="mt-4">
                        <label htmlFor="threshold" className="block text-sm font-medium text-gray-700 mb-1">
                          相似度阈值: {threshold.toFixed(2)}
                          {cachedResults.length > 0 && (
                            <span className="ml-2 text-blue-600">
                              (当前匹配: {getMatchingReposCount()} / {cachedResults.length})
                            </span>
                          )}
                        </label>
                        <input
                          type="range"
                          id="threshold"
                          min="0"
                          max="1"
                          step="0.05"
                          value={threshold}
                          onChange={handleThresholdChange}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          调整相似度阈值以控制搜索结果的精确度。值越高，匹配越精确。
                          {cachedResults.length > 0 && ' 调整滑块可实时查看不同阈值下的匹配结果。'}
                        </p>
                      </div>
                    </>
                  )}
                  
                  {/* 添加搜索说明 */}
                  <div className="mt-4 text-sm text-gray-600">
                    <p>搜索范围包括：仓库名称、描述、语言、所有者、主题标签{includeReadme ? '和README内容' : ''}</p>
                    {includeReadme && (
                      <div className="text-xs text-gray-500 mt-1">
                        <p>注意：README内容仅在搜索时获取，不会在初始加载时获取，以提高性能。</p>
                        <p>由于GitHub API限制，获取大量README可能需要较长时间，请耐心等待。</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="self-start">
                  <button
                    type="submit"
                    className="w-full md:w-auto px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                    disabled={useSemanticSearch && !apiKeyStatus.isValid}
                  >
                    搜索
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default SearchForm; 